/**
 * 平台检测工具函数
 * 对标 tv-linux 项目的 getTvdSegment 函数
 */

import { Platform } from './constants';
import { useAppStore } from '@/stores/appStore';

/**
 * 获取当前平台类型
 * 对标 tv-linux 的 getTvdSegment 函数
 *
 * 判断逻辑：
 * 1. 优先通过域名判断（iflix.com 域名为 iflix 平台）
 * 2. 优先从 store 读取 platform 参数（用于跨页面传递，与 vChannelId 统一处理）
 * 3. 通过 URL 查询参数判断（platform=tvdtv 为 tvdtv 平台，platform=iflix 为 iflix 平台）
 * 4. 其他情况默认为 wetv 平台
 *
 * @returns 平台类型 'wetv' | 'iflix' | 'tvdtv'
 */
export function getPlatformType(): Platform {
  if (typeof window === 'undefined') {
    return Platform.WETV;
  }

  const { hostname, search } = window.location;

  // iflix 域名判断
  const iflixDomains = ['tv.iflix.com', 'test.iflix.com'];
  if (iflixDomains.includes(hostname) || hostname.includes('iflix')) {
    return Platform.IFLIX;
  }

  // 优先从 store 读取 platform 参数（用于跨页面传递，与 vChannelId 统一处理）
  const storedPlatform = useAppStore.getState().platform;
  if (storedPlatform === 'tvdtv') {
    return Platform.TVDTV;
  }
  if (storedPlatform === 'iflix') {
    return Platform.IFLIX;
  }

  // 降级：通过 URL 查询参数判断平台
  const searchParams = new URLSearchParams(search);
  const platform = searchParams.get('platform');

  if (platform === 'tvdtv') {
    // 如果 URL 中有 platform 参数，保存到 store
    useAppStore.getState().setPlatform('tvdtv');
    return Platform.TVDTV;
  }
  if (platform === 'iflix') {
    // 如果 URL 中有 platform 参数，保存到 store
    useAppStore.getState().setPlatform('iflix');
    return Platform.IFLIX;
  }

  // 默认为 wetv
  return Platform.WETV;
}

/**
 * 获取平台显示名称
 * @param platform 平台类型，不传则自动检测
 * @returns 平台名称 'WeTV' | 'iflix' | 'Tencent Video'
 */
export function getPlatformName(platform?: Platform): string {
  const currentPlatform = platform || getPlatformType();

  switch (currentPlatform) {
    case Platform.IFLIX:
      return 'iflix';
    case Platform.TVDTV:
      return 'Tencent Video';
    case Platform.WETV:
    default:
      return 'WeTV';
  }
}

/**
 * 判断是否为 iflix 平台
 */
export function isIflixPlatform(): boolean {
  return getPlatformType() === Platform.IFLIX;
}

/**
 * 判断是否为 wetv 平台
 */
export function isWetvPlatform(): boolean {
  return getPlatformType() === Platform.WETV;
}

/**
 * 判断是否为 tvdtv 平台
 */
export function isTvdtvPlatform(): boolean {
  return getPlatformType() === Platform.TVDTV;
}

/**
 * 判断是否为 LG 电视平台（WebOS / NetCast）
 * 用于遥控器键码等设备相关逻辑
 */
export function isLG(): boolean {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('webos') || userAgent.includes('netcast');
}
